import { Button, Title, Subtitle } from "@tremor/react";
import Modal from "@/components/ui/Modal";
import type { IncidentDto } from "@/entities/incidents/model";
import { useIncidentActions, Status } from "@/entities/incidents/model";
import { useMemo, useState } from "react";
import { Select, VerticalRoundedList } from "@/shared/ui";
import { IncidentIconName } from "@/entities/incidents/ui";
interface Props {
  incidents: IncidentDto[];
  handleClose: () => void;
  onSuccess?: () => void;
}
export function MergeIncidentsModal({
  incidents,
  handleClose,
  onSuccess
}: Props) {
  const [destinationIncidentId, setDestinationIncidentId] = useState<string>(incidents[0].id);
  const destinationIncident = incidents.find(incident => incident.id === destinationIncidentId);
  const sourceIncidents = incidents.filter(incident => incident.id !== destinationIncidentId);
  const incidentOptions = useMemo(() => {
    return incidents.map(incident => ({
      value: incident.id,
      label: <IncidentIconName inline incident={incident} />
    }));
  }, [incidents]);
  const selectValue = useMemo(() => {
    return {
      value: destinationIncidentId,
      label: <IncidentIconName inline incident={destinationIncident!} />
    };
  }, [destinationIncidentId, destinationIncident]);
  const errors = useMemo(() => {
    const errorDict: Record<string, boolean> = {};
    if (sourceIncidents.every(i => i.status === Status.Merged)) {
      errorDict["alreadyMerged"] = true;
    }
    return errorDict;
  }, [sourceIncidents]);
  const {
    mergeIncidents
  } = useIncidentActions();
  const handleMerge = () => {
    mergeIncidents(sourceIncidents, destinationIncident!);
    handleClose();
    onSuccess?.();
  };
  return <Modal onClose={handleClose} isOpen={true} data-sentry-element="Modal" data-sentry-component="MergeIncidentsModal" data-sentry-source-file="merge-incidents-modal.tsx">
      <div className="flex flex-col gap-5">
        <div>
          <Title data-sentry-element="Title" data-sentry-source-file="merge-incidents-modal.tsx">Merge Incidents</Title>
          <Subtitle data-sentry-element="Subtitle" data-sentry-source-file="merge-incidents-modal.tsx">
            Alerts from the following incidents will be moved into the
            destination incident and the source incidents would be marked as{" "}
            <b>Merged</b>
          </Subtitle>
        </div>
        <div>
          <div className="mb-1">
            <span className="font-bold">Source Incidents</span>
            {errors.alreadyMerged && <p className="text-red-500 text-sm mt-1">
                These incidents were already merged
              </p>}
          </div>
          <VerticalRoundedList data-sentry-element="VerticalRoundedList" data-sentry-source-file="merge-incidents-modal.tsx">
            {sourceIncidents.map(incident => <IncidentIconName key={incident.id} incident={incident} />)}
          </VerticalRoundedList>
        </div>
        <div>
          <div className="mb-1">
            <span className="font-bold">Destination Incident</span>
          </div>
          <Select instanceId="merge-incidents-destination-incident-select" options={incidentOptions} value={selectValue} onChange={option => setDestinationIncidentId(option!.value)} placeholder="Select destination incident" data-sentry-element="Select" data-sentry-source-file="merge-incidents-modal.tsx" />
        </div>
      </div>
      <div className="flex justify-end mt-4 gap-2">
        <Button onClick={handleClose} color="orange" variant="secondary" data-sentry-element="Button" data-sentry-source-file="merge-incidents-modal.tsx">
          Cancel
        </Button>
        <Button onClick={handleMerge} color="orange" disabled={Object.values(errors).length != 0} data-sentry-element="Button" data-sentry-source-file="merge-incidents-modal.tsx">
          Confirm merge
        </Button>
      </div>
    </Modal>;
}